import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Divider,
  Card,
  CardContent,
  Chip,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Edit,
  EditNote,
  Delete,
  AccessTimeOutlined,
} from '@mui/icons-material';

import { CommercialACForm } from '../../../components/common/CommercialAcfrom';
import { AMCForm } from '../../../components/common/AmcForm';
import { ProblemReportForm } from '../../../components/common/Others';
import { GetAdresses, GETUsers } from '../../../services/users';
import {
  IService,
  IUser,
  IUserAddress,
} from '../../../utils/entities/interface';
import { capitalizeWords, formatPhoneNumber } from '../../../utils/helper';
import { GETServices } from '../../../services/service';
import {
  EditBooking,
  GETBookingById,
  NewBooking,
} from '../../../services/booking';
import useSnackbar from '../../../hooks/useSnackbar';
import { useNavigate, useParams } from 'react-router-dom';
import EditAddressDialog from './EditAddress';
import CreateUserModal from './AddUserModal';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../components/atoms/Button';
import { DetailPageHeader } from '../../../components/layout/DetailPageHeader';
import { DateField } from '../Details';
import { acItems } from '../../../utils/constants';
import { ServiceRow } from '../../../components/organisms/bsesrvices';

const removeZeroQuantityEntries = (
  acList: Record<string, number>,
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(acList || {}).filter(
      ([, value]) => Number(value) > 0,
    ),
  );

const AddBooking = () => {
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [userList, setUserList] = useState<IUser[] | null>(null);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [addresslist, setAddressList] = useState<IUserAddress[] | null>(null);
  const [openAddCustomer, setOpenAddCustomer] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<IUserAddress | null>(
    null,
  );
  const [isLoading, setisLoading] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(0);
  const [bookingDate, setBookingDate] = useState(() => {
    const today = new Date().toISOString().split('T')[0]; // Get current date in 'YYYY-MM-DD' format
    return today;
  });
  const [selectedSlot, setSelectedSlot] = useState<
    'FIRST_HALF' | 'SECOND_HALF'
  >('FIRST_HALF');
  const [selectedServices, setSelectedServices] = useState<
    { service: IService; serviceDetails: any }[]
  >([]);
  const [inputValue, setInputValue] = useState('');

  const isEditMode = Boolean(bookingId);

  useEffect(() => {
    fetchUserList();
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      fetchAddressList();
    }
  }, [selectedUser]);

  useEffect(() => {
    if (bookingId) fetchBookingDetails(bookingId);
  }, [bookingId]);

  const fetchBookingDetails = async (id: string) => {
    try {
      const { data } = await GETBookingById(id);

      const serviceAggregation: Record<string, any> = {};
      const selectedServicesData: any[] = [];

      if (Array.isArray(data.serviceDetails)) {
        for (const details of data.serviceDetails) {
          const key = details.service_data.key;
          const acType = details.acType?.toUpperCase();
          const quantity = parseInt(details.quantity, 10) || 0;

          // Handle special services separately
          if (
            ['COMMERCIAL_AC', 'COPPER_PIPING', 'AMC', 'OTHER'].includes(key)
          ) {
            const existingService = selectedServicesData.find(
              (s) => s.service.key === key,
            );

            if (existingService) {
              if (key === 'COMMERCIAL_AC') {
                existingService.serviceDetails.acList[acType] =
                  (existingService.serviceDetails.acList[acType] || 0) +
                  quantity;
              } else if (key === 'OTHER') {
                existingService.serviceDetails.problem = details.otherService;
                existingService.serviceDetails.comments = details.comment;
                existingService.serviceDetails.acType = details.acType || '';
              } else {
                existingService.serviceDetails.quantity += quantity;
              }
            } else {
              selectedServicesData.push({
                service: details.service_data,
                serviceDetails:
                  key === 'COMMERCIAL_AC'
                    ? {
                        acList: { [acType]: quantity },
                        services: details.services,
                      }
                    : key === 'OTHER'
                      ? {
                          problem: details.otherService,
                          comments: details.comment,
                          acType: details.acType || '',
                        }
                      : {
                          quantity: quantity,
                          place: details.place,
                          comments: details.comment,
                        },
              });
            }
            continue;
          }

          // Process normal services and group by `key`
          if (!serviceAggregation[key]) {
            serviceAggregation[key] = {
              service: details.service_data,
              serviceDetails: {}, // Change to an object instead of an array
            };
          }

          // Add or update the acType and quantity directly as key-value pairs
          if (serviceAggregation[key].serviceDetails[acType]) {
            serviceAggregation[key].serviceDetails[acType] += quantity;
          } else {
            serviceAggregation[key].serviceDetails[acType] = quantity;
          }
        }
      }

      // Convert grouped services to an array
      selectedServicesData.push(...Object.values(serviceAggregation));

      setSelectedServices(selectedServicesData);
      setSelectedSlot(data.slot);
      setSelectedUser(data.user);
      setSelectedAddress(data.addressDetails[0]);
      setBookingDate(new Date(data.date).toISOString().split('T')[0]);

      setAmount(data.amount);
    } catch (error) {
      console.error('Error fetching booking details:', error);
    }
  };

  const fetchAddressList = async () => {
    if (selectedUser && !isEditMode) {
      const fetched = await GetAdresses(selectedUser?._id);
      setAddressList(fetched.data);
      setSelectedAddress(fetched?.data?.[0]);
    }
  };

  const fetchUserList = async (search?: string) => {
    try {
      if (isEditMode) return;
      const obj: any = { limit: 50 };

      if (search && search.trim() !== '') {
        obj.searchQuery = search.trim();
      }
      const userList = await GETUsers(obj);
      setUserList(userList.data);
    } catch (error: any) {
      console.log('error', error);
    }
  };

  const resetBooking = () => {
    setSelectedUser(null);
    setOpenEditModal(false);
    setOpenAddCustomer(false);
    setSelectedAddress(null);
    setSelectedSlot('FIRST_HALF');
    setBookingDate(new Date().toISOString().split('T')[0]);
    setSelectedServices([]);
  };

  const handleCreateBooking = async () => {
    try {
      if (
        !selectedUser ||
        !selectedUser._id ||
        !selectedAddress ||
        !bookingDate
      ) {
        showSnackbar('Requried Information Missing', 'error');
        return;
      }
      setisLoading(true);

      const invalidOtherService = selectedServices.some(
        (ser) => ser.service.key === 'OTHER' && !ser.service._id,
      );
      if (invalidOtherService) {
        showSnackbar(
          'Other service is not configured. Please contact admin.',
          'error',
        );
        setisLoading(false);
        return;
      }

      const temp: any[] = [];
      selectedServices.map((ser) => {
        if (
          [
            'SERVICE',
            'REPAIR',
            'INSTALLATION',
            'COMPRESSOR',
            'GAS_CHARGING',
          ].includes(ser.service.key)
        ) {
          Object.entries(removeZeroQuantityEntries(ser.serviceDetails)).map(
            ([type, count]) => {
            temp.push({
              service_id: ser.service._id,
              service_data: ser.service,
              services: [],
              serviceType: '',
              quantity: count,
              acType: type,
              place: '',
              comment: '',
              otherService: '',
            });
          });
        }
        if (ser.service.key == 'COMMERCIAL_AC') {
          Object.entries(
            removeZeroQuantityEntries(ser.serviceDetails.acList),
          ).map(([type, count]) => {
            temp.push({
              service_id: ser.service._id,
              service_data: ser.service,
              services: ser.serviceDetails.services,
              serviceType: '',
              quantity: count,
              acType: type,
              place: '',
              comment: '',
              otherService: '',
            });
          });
        }
        if (ser.service.key == 'AMC') {
          temp.push({
            service_id: ser.service._id,
            service_data: ser.service,
            services: ser.serviceDetails.services,
            serviceType: '',
            quantity: ser.serviceDetails.quantity,
            acType: '',
            place: ser.serviceDetails.place,
            comment: ser.serviceDetails.comments,
            otherService: '',
          });
        }
        if (ser.service.key == 'COPPER_PIPING') {
          temp.push({
            service_id: ser.service._id,
            service_data: ser.service,
            services: ser.serviceDetails.services,
            serviceType: '',
            quantity: ser.serviceDetails.quantity,
            acType: '',
            place: ser.serviceDetails.place,
            comment: ser.serviceDetails.comments,
            otherService: '',
          });
        }
        if (ser.service.key == 'OTHER') {
          temp.push({
            service_id: ser.service._id,
            service_data: ser.service,
            services: [],
            serviceType: '',
            quantity: ser.serviceDetails.quantity ?? 1,
            acType: ser.serviceDetails.acType || '',
            place: '',
            comment: ser.serviceDetails.comments,
            otherService: ser.serviceDetails.problem,
          });
        }
      });

      if (temp.length <= 0) {
        showSnackbar('Booking Service Infomation Required', 'error');
        setisLoading(false);
        return;
      }

      const missingServiceId = temp.some((item) => !item.service_id);
      if (missingServiceId) {
        showSnackbar(
          'Service information is incomplete. Please re-add the service.',
          'error',
        );
        setisLoading(false);
        return;
      }

      const status = isEditMode
        ? await EditBooking({
            bookingId: bookingId as string,
            amount: amount,
            serviceDetails: temp,
            slot: selectedSlot,
            date: bookingDate,
            addressId: selectedAddress?._id,
          })
        : await NewBooking({
            user_id: selectedUser?._id,
            serviceDetails: temp,
            date: bookingDate,
            amount: 0,
            addressId: selectedAddress?._id,
            slot: selectedSlot,
            name: selectedUser.name || 'N/A',
          });
      setisLoading(false);

      console.log('status', status);
      if (status == 201) {
        showSnackbar('Booking Created', 'success');
        setTimeout(() => {
          navigate(-1);
        }, 500);
      }
      if (status == 200) {
        showSnackbar('Booking Edited Successfully', 'success');
        setTimeout(() => {
          navigate(-1);
        }, 500);
      }
      return;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save booking';
      showSnackbar(message, 'error');
    } finally {
      setisLoading(false);
    }
  };

  const reloadAddress = (updatedAddress: IUserAddress) => {
    if (updatedAddress && updatedAddress._id) {
      setSelectedAddress(updatedAddress);
      setOpenEditModal(false);
    } else {
      fetchAddressList();
      setOpenEditModal(false);
    }
  };

  const handleAddedCustomer = () => {
    fetchUserList();
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchUserList(inputValue);
    }, 400);

    return () => clearTimeout(delay);
  }, [inputValue]);

  return (
    <Box sx={{ background: '#121212', minHeight: '100vh', color: '#fff' }}>
      {SnackbarComponent}
      {/* Header Section */}
      <DetailPageHeader title={isEditMode ? 'Edit Booking' : 'Create Booking'} />
      <Box sx={{ mb: 2 }} />
      <Box
        sx={{
          background: '#1C1C1C',
          borderRadius: 4,
          p: 7,
          width: '100%',
          margin: 3,
        }}
      >
        <Grid container spacing={2}>
          {/* Select User */}
          {isEditMode && selectedUser ? (
            <ViewCustomer Customer={selectedUser} />
          ) : (
            <>
              {' '}
              <Grid item xs={12}>
                <Typography color="#8A8D91">Existing User</Typography>
              </Grid>
              <Grid item xs={10} p={2}>
                <Autocomplete
                  options={Array.isArray(userList) ? userList : []}
                  getOptionLabel={(option) =>
                    `${capitalizeWords(option.name) || 'N/A'} (${option.phoneNumber.slice(0, 10)})`
                  } // Display name and phone number
                  value={selectedUser}
                  onChange={(event, newValue) => {
                    setSelectedUser(newValue);
                  }}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  disabled={isEditMode}
                  sx={{
                    flex: 1,
                    '& .MuiAutocomplete-listbox': {
                      '&::-webkit-scrollbar': {
                        display: 'none', // Hides scrollbar for webkit browsers
                      },
                      '&::-moz-scrollbar': {
                        display: 'none', // Hides scrollbar for Firefox
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select User"
                      sx={{
                        input: { color: '#8A8D91' },
                        label: { color: '#8A8D91' },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                />
              </Grid>{' '}
              <Grid item xs={2} p={2}>
                <Button
                  variant="contained"
                  fullWidth
                  size="small"
                  sx={{
                    height: '100%',
                    backgroundColor: '#E31E25',
                    textTransform: 'none',
                    borderRadius: '8px',
                    width: '150px',
                    paddingX: 2,
                    float: 'right',
                    '&:hover': {
                      backgroundColor: '#c51b21',
                    },
                  }}
                  startIcon={<Add />}
                  disabled={isEditMode}
                  onClick={() => setOpenAddCustomer(true)}
                >
                  New User
                </Button>
              </Grid>
            </>
          )}
          <Grid item xs={12} mb={2}>
            <ServicesListComp
              selectedServices={selectedServices}
              setSelectedServices={setSelectedServices}
              showSnackbar={showSnackbar}
              isEditMode={isEditMode}
            />
          </Grid>
          <CreateUserModal
            open={openAddCustomer}
            onClose={() => setOpenAddCustomer(false)}
            onSaved={handleAddedCustomer}
          />
          {/* Booking Details */}
          <Grid item xs={6}>
            <Typography color="gray">Booking Date</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography color="gray">Booking Slot</Typography>
          </Grid>
          <Grid item xs={6}>
            <DateField
              type="date"
              value={bookingDate || ''}
              onChange={(e) => setBookingDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <ToggleButtonGroup
              value={selectedSlot}
              exclusive
              onChange={(e, newSlot) => setSelectedSlot(newSlot)}
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid #666',
                background: 'transparent',
              }}
            >
              {['FIRST_HALF', 'SECOND_HALF'].map((slot) => (
                <ToggleButton
                  key={slot}
                  value={slot}
                  sx={{
                    flex: 1,
                    color: '#8A8D91',
                    fontWeight: 500,
                    textTransform: 'none',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    background: selectedSlot === slot ? '#000' : 'transparent',
                    '&.Mui-selected': {
                      background: '#000',
                      color: '#8A8D91',
                    },
                    '&:not(:last-of-type)': {
                      borderRight: '1px solid #666',
                    },
                    '&:hover': {
                      background: selectedSlot === slot ? '#000' : '#1a1a1a',
                    },
                  }}
                >
                  {slot === 'FIRST_HALF' ? 'First Half' : 'Second Half'}
                  <AccessTimeOutlined sx={{ fontSize: 16 }} />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          <ServiceAddressComp
            selectedUser={selectedUser}
            selectedAddress={selectedAddress}
            onReload={reloadAddress}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 5,
              marginBottom: 1,
              width: '100%',
              gap: 2,
            }}
          >
            <SecondaryButton title="Cancel" onClick={resetBooking} />
            <PrimaryButton
              title={isEditMode ? 'Save' : 'Save'}
              onClick={handleCreateBooking}
              disabled={isLoading}
            />
          </Box>
        </Grid>{' '}
      </Box>{' '}
    </Box>
  );
};

export default AddBooking;

const ServiceAddressComp = ({
  selectedUser,
  selectedAddress,
  onReload,
}: {
  selectedUser: IUser | null;
  selectedAddress: IUserAddress | null;
  onReload: (updatedAddress: IUserAddress) => void;
}) => {
  const [isEditMode, setOpenEditModal] = useState<boolean>(false);
  const [isAddMode, setOpenAddModal] = useState<boolean>(false);
  return (
    <>
      {' '}
      <Grid item xs={12} mt={2}>
        <Typography color="gray">Service Address</Typography>
      </Grid>
      {/* Address Section */}
      <Grid item xs={10}>
        {selectedUser && selectedAddress ? (
          <>
            <Typography variant="body2" sx={{ color: '#ccc' }}>
              {selectedAddress?.house &&
                `${capitalizeWords(selectedAddress.house)}, `}
              {selectedAddress?.street &&
                capitalizeWords(selectedAddress.street)}
              <br />
              {selectedAddress?.city && capitalizeWords(selectedAddress.city)}
              <br />
              {selectedAddress?.landmark &&
                `Landmark: ${capitalizeWords(selectedAddress.landmark)}`}
              <br />
              {selectedAddress?.state &&
                capitalizeWords(selectedAddress.state)}{' '}
              - {selectedAddress?.zipcode}
            </Typography>

            {/* Label + Edit Icon in Dark Style */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 1.2,
                px: 1.5,
                py: 0.75,
                bgcolor: 'rgba(255, 255, 255, 0.05)', // soft dark bg
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2,
                width: 'fit-content',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  bgcolor: '#388e3c', // muted green
                  color: 'white',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {capitalizeWords(selectedAddress.saveAs)}
              </Typography>

              <IconButton
                size="small"
                sx={{
                  color: '#aaa',
                  p: '4px',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#f44336', // subtle red on hover
                  },
                }}
                onClick={() => setOpenEditModal(true)}
              >
                <EditNote fontSize="small" />
              </IconButton>
            </Box>
          </>
        ) : (
          <Typography color="gray" variant="caption">
            Address Not Found
          </Typography>
        )}
      </Grid>
      <Grid item xs={2}>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#E31E25',
            textTransform: 'none',
            height: '65px',
            borderRadius: '8px',
            float: 'right',
            mr: 2,
            width: '150px',
            paddingX: 2,
            '&:hover': {
              backgroundColor: '#c51b21',
            },
          }}
          startIcon={selectedAddress ? <Edit /> : <Add />}
          onClick={() => setOpenAddModal(true)}
        >
          {'Add Address'}
        </Button>
      </Grid>
      {selectedUser && selectedUser?._id && (
        <>
          <EditAddressDialog
            open={isEditMode}
            onClose={() => setOpenEditModal(false)}
            exisitingAddress={selectedAddress}
            onSave={onReload}
            userId={selectedUser._id}
          />
          <EditAddressDialog
            open={isAddMode}
            onClose={() => setOpenAddModal(false)}
            onSave={onReload}
            userId={selectedUser._id}
          />
        </>
      )}
    </>
  );
};

interface ServicesListCompProps {
  selectedServices: { service: IService; serviceDetails: any }[];
  setSelectedServices: React.Dispatch<
    React.SetStateAction<{ service: IService; serviceDetails: any }[]>
  >;
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
  ) => void;
  isEditMode?: boolean;
}
const sortServices = (services: IService[]) => {
  return services.sort((a, b) => {
    const order = {
      SERVICE: 0,
      REPAIR: 1,
      INSTALLATION: 2,
      COMPRESSOR: 3,
      GAS_CHARGING: 4,
      COMMERCIAL_AC: 5,
      AMC: 6,
      COPPER_PIPING: 7,
      OTHER: 8,
    };

    return (
      order[a.key as keyof typeof order] - order[b.key as keyof typeof order]
    );
  });
};

const resolveServiceFromCatalog = (
  key: string,
  catalog: IService[] | null,
  hints: (IService | null | undefined)[] = [],
): IService => {
  const fromCatalog = catalog?.find((service) => service.key === key);
  if (fromCatalog?._id) return fromCatalog;

  const fromHint = hints.find((service) => service?.key === key && service?._id);
  if (fromHint) return fromHint;

  return { key } as IService;
};

const ServicesListComp: React.FC<ServicesListCompProps> = ({
  selectedServices,
  setSelectedServices,
  showSnackbar,
  isEditMode = false,
}) => {
  const [currDirty, setCurrDirty] = useState<IService | null>(null);
  const [services, setServices] = useState<IService[] | null>(null);
  const [tempServiceDetails, setTempServiceDetails] = useState<any>(null);
  const [showServices, setShowServices] = useState<boolean>(isEditMode);
  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await GETServices({ limit: 100, page: 1 });
      setServices(sortServices(response.data || []));
    } catch (err) {
      console.log('#ERROR !while fetching services');
    }
  };

  const getServiceRecord = (
    key: string,
    ...hints: (IService | null | undefined)[]
  ) => resolveServiceFromCatalog(key, services, [currDirty, ...hints]);

  const isEmptyServiceDetails = (details: any) => {
    if (!details) return true;

    // completely empty {}
    if (Object.keys(details).length === 0) return true;

    // COMMERCIAL_AC case
    if (
      'acList' in details &&
      'services' in details &&
      Object.keys(details.acList || {}).length === 0 &&
      (details.services || []).length === 0
    ) {
      return true;
    }

    if (
      'quantity' in details &&
      details.quantity === 0 &&
      (!details.comments || details.comments.trim() === '')
    ) {
      return true;
    }

    if ('problem' in details || 'comments' in details) {
      const problem = String(details.problem || '').trim();
      const comments = String(details.comments || '').trim();
      return !problem && !comments;
    }

    const values = Object.values(details);
    if (
      values.length > 0 &&
      values.every((val) => !val || Number(val) === 0)
    ) {
      return true;
    }

    return false;
  };

  const toggleService = (service: IService) => {
    setCurrDirty(service);

    setSelectedServices((prev) => {
      const isSelected = prev.some((s) => s.service.key === service.key);

      let updatedList = [...prev];

      if (isSelected) {
        // ❌ remove if already selected
        updatedList = updatedList.filter((s) => s.service.key !== service.key);
      } else {
        // ✅ add with default structure
        let newService;

        if (service.key === 'COMMERCIAL_AC') {
          newService = {
            service,
            serviceDetails: { acList: {}, services: [] },
          };
        } else if (service.key === 'AMC' || service.key === 'COPPER_PIPING') {
          newService = {
            service,
            serviceDetails: {
              quantity: 0,
              place: 'Residential',
              comments: '',
            },
          };
        } else if (service.key === 'OTHER') {
          newService = {
            service,
            serviceDetails: {
              problem: '',
              comments: '',
              acType: '',
              quantity: 1,
            },
          };
        } else {
          newService = {
            service,
            serviceDetails: {},
          };
        }

        updatedList.push(newService);
      }

      // 🔥 FINAL STEP: remove all empty serviceDetails
      return updatedList.filter(
        (item) => !isEmptyServiceDetails(item.serviceDetails),
      );
    });
  };

  const handleCommercialACUpdate = (
    acList: Record<string, number>,
    services: string[],
  ) => {
    const isAcListEmpty = !acList || Object.keys(acList).length === 0;

    const isServicesEmpty = !services || services.length === 0;
    if (isAcListEmpty && isServicesEmpty) {
      showSnackbar('Please Select Any Value', 'error');
      return;
    }
    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.service.key === 'COMMERCIAL_AC');

      if (exists) {
        return prev.map((s) =>
          s.service.key === 'COMMERCIAL_AC'
            ? {
                service: getServiceRecord('COMMERCIAL_AC', s.service),
                serviceDetails: {
                  acList,
                  services,
                },
              }
            : s,
        );
      }

      return [
        ...prev,
        {
          service: getServiceRecord('COMMERCIAL_AC'),
          serviceDetails: {
            acList,
            services,
          },
        },
      ];
    });
  };
  const updateAMCDetails = (
    quantity: number,
    place: 'Residential' | 'Commercial',
    comments: string,
  ) => {
    const isQuantityInvalid = !quantity || quantity <= 0;

    if (isQuantityInvalid) {
      showSnackbar('Quantity must be greater than 0', 'error');
      return;
    }

    // ✅ Update only if valid
    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.service.key === 'AMC');

      if (exists) {
        return prev.map((s) =>
          s.service.key === 'AMC'
            ? {
                service: getServiceRecord('AMC', s.service),
                serviceDetails: { quantity, place, comments },
              }
            : s,
        );
      }

      return [
        ...prev,
        {
          service: getServiceRecord('AMC'),
          serviceDetails: { quantity, place, comments },
        },
      ];
    });
  };

  const updateCopperPipingDetails = (
    quantity: number,
    place: 'Residential' | 'Commercial',
    comments: string,
  ) => {
    const isQuantityInvalid = !quantity || quantity <= 0;

    if (isQuantityInvalid) {
      showSnackbar('Quantity must be greater than 0', 'error');
      return;
    }

    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.service.key === 'COPPER_PIPING');

      if (exists) {
        return prev.map((s) =>
          s.service.key === 'COPPER_PIPING'
            ? {
                service: getServiceRecord('COPPER_PIPING', s.service),
                serviceDetails: { quantity, place, comments },
              }
            : s,
        );
      }

      // ❗ If not exists → add it
      return [
        ...prev,
        {
          service: getServiceRecord('COPPER_PIPING'),
          serviceDetails: { quantity, place, comments },
        },
      ];
    });
  };

  const handleProblemReportUpdate = (problem: string, comments: string, acType: string) => {
    const isAcTypeEmpty = !acType || acType.trim() === '';
    const isProblemEmpty = !problem || problem.trim() === '';
    const isCommentsEmpty = !comments || comments.trim() === '';

    if (isAcTypeEmpty) {
      showSnackbar('Please select an AC type', 'error');
      return;
    }

    if (isProblemEmpty) {
      showSnackbar('Please select or enter problem', 'error');
      return;
    }

    if (isCommentsEmpty) {
      showSnackbar('Please enter comments', 'error');
      return;
    }

    setSelectedServices((prev) => {
      const exists = prev.some((s) => s.service.key === 'OTHER');
      const otherService = getServiceRecord(
        'OTHER',
        exists ? prev.find((s) => s.service.key === 'OTHER')?.service : undefined,
      );

      if (!otherService._id) {
        showSnackbar('Other service is not configured. Please contact admin.', 'error');
        return prev;
      }

      if (exists) {
        return prev.map((s) =>
          s.service.key === 'OTHER'
            ? {
                service: otherService,
                serviceDetails: { problem, comments, acType, quantity: 1 },
              }
            : s,
        );
      }

      return [
        ...prev,
        {
          service: otherService,
          serviceDetails: { problem, comments, acType, quantity: 1 },
        },
      ];
    });
  };
  return (
    <Grid
      container
      spacing={1}
      sx={{
        borderRadius: 3,
        backgroundColor: '#232323',
        padding: 2,
      }}
    >
      <Grid item xs={12} mb={1}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',

            height: 60,
          }}
        >
          <Typography gutterBottom color="#8A8D91" flex={2}>
            All Services
          </Typography>

          {!showServices && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#E31E25',
                textTransform: 'none',
                height: '100%',
                borderRadius: '8px',
                width: '150px',
                paddingX: 2,
                '&:hover': {
                  backgroundColor: '#c51b21',
                },
              }}
              startIcon={<Add />}
              onClick={() => setShowServices((prev) => !prev)}
            >
              {'Add Services'}
            </Button>
          )}
        </Box>
      </Grid>

      {showServices && (
        <>
          <Grid container spacing={1} sx={{ p: 1 }}>
            {services?.map((service) => {
              const isSelected = selectedServices.some(
                (s) => s.service.key === service.key,
              );
              const isCurrent = currDirty?.key === service.key;
              return (
                <Grid item key={service._id} md={3} p={1}>
                  <Button
                    variant={isCurrent ? 'contained' : 'outlined'}
                    disableElevation
                    fullWidth
                    color={isCurrent ? 'error' : 'inherit'}
                    onClick={() => toggleService(service)}
                    disabled={isSelected && !isCurrent}
                    sx={{
                      minWidth: 120,
                      minHeight: 38,
                      borderRadius: '8px',
                      textTransform: 'capitalize',
                      fontWeight: 600,
                      fontSize: '0.97rem',
                      px: 3,
                      py: 1,
                      margin: 0,
                      backgroundColor: isCurrent ? '#E31E25' : 'transparent',
                      border: isCurrent ? 'none' : '2px solid #E31E25',
                      color: '#fff',
                      transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: isCurrent
                          ? '#B71C1C'
                          : 'rgba(211,47,47,0.07)',
                        border: isCurrent ? 'none' : '2px solid #E31E25',
                        color: isCurrent ? '#fff' : '#E31E25',
                        boxShadow: 'none',
                      },
                      '&:disabled': {
                        color: '#888',
                        borderColor: '#888',
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    {capitalizeWords(service.key.replace('_', ' '))}
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          {currDirty && (
            <Grid item xs={8}>
              {currDirty &&
                [
                  'SERVICE',
                  'REPAIR',
                  'INSTALLATION',
                  'COMPRESSOR',
                  'GAS_CHARGING',
                ].includes(currDirty.key) && (
                  <AcList
                    details={
                      selectedServices.find(
                        (s) => s.service.key === currDirty.key,
                      )?.serviceDetails ||
                      tempServiceDetails ||
                      {}
                    }
                    acItems={acItems}
                    service={currDirty}
                    setSelectedServices={setSelectedServices}
                    setCurrDirty={setCurrDirty}
                    showSnackbar={showSnackbar}
                  />
                )}{' '}
              {currDirty?.key === 'COMMERCIAL_AC' && (
                <CommercialACForm
                  existingDetails={
                    selectedServices.find(
                      (s) => s.service.key === 'COMMERCIAL_AC',
                    )?.serviceDetails || { acList: {}, services: [] }
                  }
                  onUpdate={handleCommercialACUpdate}
                  setCurrDirty={setCurrDirty}
                  showSnackbar={showSnackbar}
                />
              )}
              {currDirty?.key === 'AMC' && (
                <AMCForm
                  existingDetails={
                    selectedServices.find((s) => s.service.key === 'AMC')
                      ?.serviceDetails || {
                      quantity: 0,

                      place: 'Residential',
                      comments: '',
                    }
                  }
                  label="Amc"
                  onUpdate={updateAMCDetails}
                  setCurrDirty={setCurrDirty}
                  showSnackbar={showSnackbar}
                />
              )}
              {currDirty?.key === 'COPPER_PIPING' && (
                <AMCForm
                  existingDetails={
                    selectedServices.find(
                      (s) => s.service.key === 'COPPER_PIPING',
                    )?.serviceDetails || {
                      quantity: 0,

                      place: 'Residential',
                      comments: '',
                    }
                  }
                  label="Copper Piping"
                  onUpdate={updateCopperPipingDetails}
                  setCurrDirty={setCurrDirty}
                  showSnackbar={showSnackbar}
                />
              )}{' '}
              {currDirty?.key === 'OTHER' && (
                <ProblemReportForm
                  existingDetails={
                    selectedServices.find((s) => s.service.key === 'OTHER')
                      ?.serviceDetails || { problem: '', comments: '', acType: '' }
                  }
                  onUpdate={handleProblemReportUpdate}
                  setCurrDirty={setCurrDirty}
                  showSnackbar={showSnackbar}
                />
              )}
            </Grid>
          )}

          <Grid item xs={12} mt={1}>
            <ViewServiceDetails
              services={selectedServices}
              setCurrDirty={setCurrDirty}
              setSelectedServices={setSelectedServices}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

interface AcListProps {
  details: { [key: string]: number };
  service: IService;
  setSelectedServices: React.Dispatch<React.SetStateAction<any>>;
  setCurrDirty: React.Dispatch<React.SetStateAction<any>>;
  acItems: { name: string }[];
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
  ) => void;
  // Array of AC types, e.g. [{name: "Split AC"}, ...]
}
const AcList: React.FC<AcListProps> = ({
  details,
  service,
  setSelectedServices,
  setCurrDirty,
  acItems,
  showSnackbar,
}) => {
  const [tempServiceDetails, setTempServiceDetails] = useState(details || {});

  useEffect(() => {
    setTempServiceDetails(details || {});
  }, [details, service]);

  const handleCountChange = (name: string, newCount: number) => {
    setTempServiceDetails((prevDetails) => {
      if (newCount <= 0) {
        const { [name]: _, ...rest } = prevDetails;
        return rest;
      }
      return { ...prevDetails, [name]: newCount };
    });
  };

  const handleSave = () => {
    const cleanedDetails = removeZeroQuantityEntries(tempServiceDetails);

    const isEmpty =
      !cleanedDetails || Object.keys(cleanedDetails).length === 0;

    if (isEmpty) {
      showSnackbar('Please Select Any AC', 'error');
      return;
    }

    setSelectedServices(
      (prevServices: { service: IService; serviceDetails: any }[]) => {
        const updatedServices = prevServices.map((s) =>
          s.service.key === service.key
            ? { ...s, serviceDetails: cleanedDetails }
            : s,
        );

        return updatedServices.some((s) => s.service.key === service.key)
          ? updatedServices
          : [
              ...updatedServices,
              { service, serviceDetails: cleanedDetails },
            ];
      },
    );

    setCurrDirty(null);
  };

  return (
    <Box sx={{ backgroundColor: '#1C1C1C', p: 2, borderRadius: '8px' }}>
      <Grid container spacing={0} alignItems="center">
        {Array.isArray(acItems) &&
          acItems.map((ac, index) => (
            <Grid item xs={6} key={index} sx={{ mb: 1.5 }}>
              <Box display="flex" alignItems="center" justifyContent={'center'}>
                {/* Left: Name */}
                <Typography
                  sx={{
                    color: '#cccccc',
                    fontSize: '1.1rem',
                    fontWeight: 400,
                    minWidth: 112, // ensures names line up vertically
                    mr: 2, // less gap
                  }}
                >
                  {ac.name}
                </Typography>
                {/* Middle: Controls */}
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    background: '#232323',
                    borderRadius: '6px',
                    pl: 0.8,
                    pr: 0.8,
                    minWidth: '108px',
                  }}
                >
                  <Button
                    onClick={() =>
                      handleCountChange(
                        ac.name,
                        Math.max(
                          0,
                          Number(tempServiceDetails[ac.name] || 0) - 1,
                        ),
                      )
                    }
                    variant="outlined"
                    sx={{
                      minWidth: '28px',
                      color: '#cccccc',
                      borderColor: '#444',
                      borderRadius: '5px',
                      p: 0,
                      fontWeight: 600,
                      fontSize: '1.15rem',
                      height: '30px',
                      lineHeight: 1,
                    }}
                  >
                    -
                  </Button>
                  <Box
                    sx={{
                      width: '32px',
                      textAlign: 'center',
                      color: '#cccccc',
                      fontWeight: 600,
                      fontSize: '1.05rem',
                      mx: 0.5,
                    }}
                  >
                    {Number(tempServiceDetails[ac.name] || 0)}
                  </Box>
                  <Button
                    onClick={() =>
                      handleCountChange(
                        ac.name,
                        Number(tempServiceDetails[ac.name] || 0) + 1,
                      )
                    }
                    variant="outlined"
                    sx={{
                      minWidth: '28px',
                      color: '#cccccc',
                      borderColor: '#444',
                      borderRadius: '5px',
                      p: 0,
                      fontWeight: 600,
                      fontSize: '1.15rem',
                      height: '30px',
                      lineHeight: 1,
                    }}
                  >
                    +
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        {/* Done button: center under the columns */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#E31E25',
              color: 'white',
              borderRadius: '8px',
              fontWeight: 500,
              minWidth: 100,
              px: 3,
              height: 38,
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
            onClick={handleSave}
          >
            Done
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

const ViewServiceDetails = ({
  services,
  setCurrDirty,
  setSelectedServices,
  readonly = true, // let parent control editability
}: {
  services: { service: IService; serviceDetails: any }[]; // `any` because serviceDetails shape varies by type
  setCurrDirty: React.Dispatch<React.SetStateAction<IService | null>>;
  setSelectedServices: React.Dispatch<React.SetStateAction<any>>;
  readonly?: boolean;
}) => {
  const handleEdit = (service: IService) => setCurrDirty(service);

  const handleDelete = (service: IService) => {
    console.log('service', service);

    setSelectedServices((prev: any) =>
      prev.filter((s: any) => s.service.key !== service.key),
    );
  };

  // Simple map function to turn serviceDetails into row props for ServiceRow:
  const serviceToRowProps = (serviceObj: {
    service: IService;
    serviceDetails: any;
  }) => {
    const { service, serviceDetails } = serviceObj;
    const key = service.key;

    if (
      [
        'SERVICE',
        'REPAIR',
        'INSTALLATION',
        'COMPRESSOR',
        'GAS_CHARGING',
      ].includes(key)
    ) {
      return {
        label: service.key.replace('_', ' '),
        items: Object.entries(removeZeroQuantityEntries(serviceDetails)).map(
          ([type, quantity]) => ({
          label: `${type}`,
          value:
            typeof quantity === 'string' || typeof quantity === 'number'
              ? quantity
              : String(quantity),
        }),
        ),
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }

    if (key === 'COMMERCIAL_AC') {
      return {
        label: 'Commercial AC',
        items: Object.entries(
          removeZeroQuantityEntries(serviceDetails.acList ?? {}),
        ).map(([type, quantity]) => ({
            label: type,
            value:
              typeof quantity === 'string' || typeof quantity === 'number'
                ? quantity
                : String(quantity),
          })),
        chips: Array.isArray(serviceDetails.services)
          ? serviceDetails.services
          : [],
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }

    if (key === 'AMC') {
      return {
        label: 'AMC',
        items: [
          { label: serviceDetails.place },
          {
            label: 'Quantity',
            value:
              typeof serviceDetails.quantity === 'string' ||
              typeof serviceDetails.quantity === 'number'
                ? serviceDetails.quantity
                : String(serviceDetails.quantity),
          },
        ],
        comment: serviceDetails.comments,
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }

    if (key === 'COPPER_PIPING') {
      return {
        label: 'Copper Piping',
        items: [
          { label: serviceDetails.place },
          {
            label: 'Quantity',
            value:
              typeof serviceDetails.quantity === 'string' ||
              typeof serviceDetails.quantity === 'number'
                ? serviceDetails.quantity
                : String(serviceDetails.quantity),
          },
        ],
        comment: serviceDetails.comments,
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }

    if (key === 'OTHER') {
      return {
        label: 'Other',
        items: [
          { label: 'AC Type', value: serviceDetails.acType || 'N/A' },
          { label: 'Problem', value: serviceDetails.problem },
        ],
        comment: serviceDetails.comments,
        readonly: false,
        onEdit: () => handleEdit(service),
        onClose: () => handleDelete(service),
      };
    }
    return null;
  };

  return (
    <CardContent sx={{ padding: 0 }}>
      {Array.isArray(services) &&
        services.map((serviceObj, idx) => {
          const rowProps = serviceToRowProps(serviceObj);
          return rowProps ? (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <ServiceRow {...rowProps} />
            </Box>
          ) : null;
        })}
    </CardContent>
  );
};

const ViewCustomer = ({ Customer }: { Customer: IUser }) => {
  return (
    <>
      {' '}
      <Grid container justifyContent="center">
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          sx={{
            backgroundColor: '#232323',
            color: '#FFFFFF',
            borderRadius: 2,
            p: 2,
            // boxShadow: 3,
            margin: 1,
          }}
        >
          <Box sx={{ width: '80%', margin: 'auto' }}>
            <Box>
              <Grid container spacing={2} alignItems="center">
                {/* Full Name & Contact Number */}
                <Grid item xs={12}>
                  <Box display="flex">
                    <Typography flex={1} color="#8A8D91">
                      Full Name
                    </Typography>
                    <Typography flex={1} color={'#FFFFFF'}>
                      {Customer?.name || 'N/A'}
                    </Typography>{' '}
                    <Typography flex={1} color="#8A8D91">
                      Contact Number
                    </Typography>
                    <Typography flex={1} color={'#FFFFFF'}>
                      {formatPhoneNumber(Customer?.phoneNumber)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Email & Customer Type */}
                <Grid item xs={12}>
                  <Box display="flex">
                    <Typography flex={1} color="#8A8D91">
                      Email Address
                    </Typography>
                    <Typography flex={1} color={'#FFFFFF'}>
                      {Customer?.email || 'N/A'}
                    </Typography>{' '}
                    <Typography flex={1} color="#8A8D91">
                      Customer Type
                    </Typography>
                    <Typography flex={1} color={'#FFFFFF'}>
                      {Customer?.type
                        ? capitalizeWords(Customer?.type).replace('_', ' ')
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
